/**
 * Editable Cell Component
 *
 * Enables inline editing of task fields directly in the table.
 * Supports text, select, and date input types.
 * Changes are saved on blur or selection.
 */

import React, { useState, useRef, useEffect } from "react";
import { Input, Select, DatePicker, Tag, message, Spin } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { TASK_PRIORITY_CONFIG } from "../task-priority-config";
import { TaskDateShortcutChips } from "./TaskQuickOptions";

/**
 * Lighten a hex color for background use
 */
const lightenColor = (color, amount = 0.9) => {
    if (!color || !color.startsWith('#')) return '#fafafa';
    const num = parseInt(color.slice(1), 16);
    const r = Math.min(255, Math.round((num >> 16) + (255 - (num >> 16)) * amount));
    const g = Math.min(255, Math.round(((num >> 8) & 0x00FF) + (255 - ((num >> 8) & 0x00FF)) * amount));
    const b = Math.min(255, Math.round((num & 0x0000FF) + (255 - (num & 0x0000FF)) * amount));
    return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Render a value based on its type (for display mode)
 */
const renderValue = (type, value, options, customRender, statusColor) => {
    if (customRender) {
        return customRender(value);
    }

    switch (type) {
        case "select": {
            const option = options?.find((opt) => opt.value === value);
            return option?.label || value || "-";
        }
        case "date":
            return value ? dayjs(value).format("DD MMM YYYY") : "-";
        case "priority": {
            if (!value) return "-";
            const priorityConfig = TASK_PRIORITY_CONFIG[value];
            const colors = priorityConfig
                ? { bg: priorityConfig.color, text: "#fff" }
                : { bg: "#d9d9d9", text: "#666" };
            return (
                <Tag
                    style={{
                        backgroundColor: colors.bg,
                        color: colors.text,
                        border: "none",
                        fontWeight: 500,
                        margin: 0,
                    }}
                >
                    {priorityConfig?.label || value}
                </Tag>
            );
        }
        case "status": {
            // Support dynamic status colors from options
            const statusOption = options?.find((opt) => opt.value === value);
            const dynamicColor = statusColor || statusOption?.color;

            const statusConfig = {
                NEW: { color: "#374151", bg: "#f3f4f6", label: "New" },
                DONE: { color: "#52c41a", bg: "#f6ffed", label: "Done" },
                WAITING: { color: "#7A4F00", bg: "#fffbe6", label: "Waiting" },
                BLOCKED: { color: "#991B1B", bg: "#fff2f0", label: "Blocked" },
                IN_REVIEW: { color: "#faad14", bg: "#fffbe6", label: "In Review" },
                CHANGES_REQUESTED: { color: "#fa8c16", bg: "#fff7e6", label: "Changes Requested" },
                PAUSED: { color: "#4B5563", bg: "#f3f4f6", label: "Paused" },
                IN_PROGRESS: { color: "#1B1B1B", bg: "#f3f4f6", label: "In Progress" },
                ACCEPTED: { color: "#8c8c8c", bg: "#fafafa", label: "Accepted" },
                CANCELLED: { color: "#525252", bg: "#f5f5f5", label: "Cancelled" },
            };

            if (dynamicColor) {
                const bgColor = lightenColor(dynamicColor, 0.85);
                const label = statusOption?.label || value;
                return (
                    <Tag
                        style={{
                            backgroundColor: bgColor,
                            color: dynamicColor,
                            border: `1px solid ${dynamicColor}`,
                            margin: 0,
                        }}
                    >
                        {label}
                    </Tag>
                );
            }

            const config = statusConfig[value] || statusConfig.ACCEPTED;
            return (
                <Tag
                    style={{
                        backgroundColor: config.bg,
                        color: config.color,
                        border: `1px solid ${config.color}`,
                        margin: 0,
                    }}
                >
                    {config.label}
                </Tag>
            );
        }
        default:
            return value || "-";
    }
};

function EditableCell({
    type = "text",
    value,
    options = [],
    onSave,
    editable = true,
    customRender,
    placeholder,
    style = {},
    statusColor, // Optional: dynamic color for status type
}) {
    const [editing, setEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    // Update tempValue when value prop changes
    useEffect(() => {
        setTempValue(value);
    }, [value]);

    // Focus input when entering edit mode
    useEffect(() => {
        if (editing && inputRef.current) {
            // For date picker, we need to handle differently
            if (type !== "date" && inputRef.current.focus) {
                inputRef.current.focus();
            }
        }
    }, [editing, type]);

    const handleSave = async (newValue) => {
        // If value hasn't changed, just exit edit mode
        if (newValue === value || (newValue === undefined && value === undefined)) {
            setEditing(false);
            return;
        }

        setLoading(true);
        try {
            await onSave(newValue);
            setEditing(false);
        } catch (error) {
            message.error("Failed to save changes");
            setTempValue(value); // Revert on error
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setTempValue(value);
        setEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            handleCancel();
        }
    };

    // Non-editable mode: just render the value
    if (!editable) {
        return (
            <div className="editable-cell-value-wrap" style={style}>
                {renderValue(type, value, options, customRender, statusColor)}
            </div>
        );
    }

    // Display mode
    if (!editing) {
        return (
            <div
                className="editable-cell-value-wrap editable"
                onClick={(e) => {
                    e.stopPropagation();
                    setEditing(true);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditing(true);
                    }
                }}
                style={{
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: 4,
                    transition: "background-color 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    minHeight: 32,
                    ...style,
                }}
                title="Click to edit"
                role="button"
                tabIndex={0}
                aria-label={`Edit ${type} value: ${value || 'empty'}. Press Enter to edit.`}
            >
                {renderValue(type, value, options, customRender, statusColor)}
                <EditOutlined
                    style={{
                        fontSize: 12,
                        color: "#999",
                        opacity: 0.5,
                        marginLeft: 4,
                    }}
                    className="edit-icon"
                    aria-hidden="true"
                />
            </div>
        );
    }

    // Edit mode - render appropriate input
    const commonProps = {
        onKeyDown: handleKeyDown,
        disabled: loading,
    };

    switch (type) {
        case "select":
        case "priority":
        case "status":
            return (
                <div onClick={(e) => e.stopPropagation()}>
                    <Select
                        {...commonProps}
                        ref={inputRef}
                        value={tempValue}
                        onChange={(val) => {
                            setTempValue(val);
                            handleSave(val);
                        }}
                        onBlur={() => {
                            if (!loading) handleCancel();
                        }}
                        autoFocus
                        open
                        style={{ width: "100%", minWidth: 100 }}
                        options={options}
                        loading={loading}
                        size="small"
                    />
                </div>
            );

        case "date": {
            const saveDateValue = (date) => {
                const isoDate = date ? date.toISOString() : null;
                setTempValue(isoDate);
                handleSave(isoDate);
            };
            return (
                <div className="task-quick-date-field task-quick-date-field--editable-cell" onClick={(e) => e.stopPropagation()}>
                    <TaskDateShortcutChips onSelect={saveDateValue} />
                    <DatePicker
                        {...commonProps}
                        ref={inputRef}
                        value={tempValue ? dayjs(tempValue) : null}
                        onChange={saveDateValue}
                        onOpenChange={(open) => {
                            if (!open && !loading) {
                                setEditing(false);
                            }
                        }}
                        autoFocus
                        open
                        size="small"
                        style={{ width: "100%" }}
                        placeholder={placeholder || "Select date"}
                    />
                </div>
            );
        }

        default:
            return (
                <div onClick={(e) => e.stopPropagation()}>
                    <Input
                        {...commonProps}
                        ref={inputRef}
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        onBlur={() => handleSave(tempValue)}
                        onPressEnter={() => handleSave(tempValue)}
                        placeholder={placeholder}
                        size="small"
                        suffix={loading ? <Spin size="small" /> : null}
                    />
                </div>
            );
    }
}

export default EditableCell;

// Export utility for use in columns
export { renderValue };
